"use client";

import { AuthGuard } from "@/components/guards/auth-guard";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { useCurrentUser } from "@/hooks/use-current-user";
import { api } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { type MouseEvent, useEffect, useState } from "react";

export type ListingFavoriteButtonProps = {
  listingId: number;
  variant?: "floating" | "button";
  favoriteLabel?: string;
  unfavoriteLabel?: string;
  invert?: boolean;
};

export function ListingFavoriteButton({
  listingId,
  variant = "floating",
  favoriteLabel,
  unfavoriteLabel,
  invert = false,
}: ListingFavoriteButtonProps) {
  const { user } = useCurrentUser();
  const [favoriteListingId, setFavoriteListingId] = useState<number | null>(
    null,
  );

  const createFavoriteListingMutation =
    api.listings.favorites.create.useMutation();
  const destroyFavoriteListingMutation =
    api.listings.favorites.destroy.useMutation();

  const { data, isLoading } = api.listings.favorites.getForListing.useQuery(
    {
      listingId,
    },
    {
      enabled: !!user,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  async function favoriteListing() {
    try {
      const { favoriteListing } =
        await createFavoriteListingMutation.mutateAsync({ id: listingId });
      setFavoriteListingId(favoriteListing.id);
    } catch (err) {
      console.error(err);
    }
  }

  async function unFavoriteListing() {
    if (!favoriteListingId) {
      return;
    }

    try {
      await destroyFavoriteListingMutation.mutateAsync({
        id: favoriteListingId,
      });

      setFavoriteListingId(null);
    } catch (err) {
      console.error(err);
    }
  }

  async function toggleFavorite() {
    if (favoriteListingId) {
      await unFavoriteListing();
    } else {
      await favoriteListing();
    }
  }

  async function handleButtonClick(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();

    await toggleFavorite();
  }

  useEffect(() => {
    if (!data) {
      return;
    }

    setFavoriteListingId(data.favoriteListing?.id ?? null);
  }, [data]);

  if (isLoading) {
    return null;
  }

  if (variant === "floating") {
    return (
      <AuthGuard onLoggedIn={toggleFavorite} title="Sign in to save this listing">
        <button
          type="button"
          onClick={handleButtonClick}
          disabled={
            createFavoriteListingMutation.isPending ||
            destroyFavoriteListingMutation.isPending
          }
        >
          <Icon
            icon="FavoriteSolid"
            className={cn("w-5", {
              "text-primary/60": !favoriteListingId,
              "text-[#F43F5E]": favoriteListingId,
              "opacity-50":
                createFavoriteListingMutation.isPending ||
                destroyFavoriteListingMutation.isPending,
            })}
            stroke="#E4E4E7"
            strokeWidth={2}
            overflow="visible"
          />
        </button>
      </AuthGuard>
    );
  }

  if (variant === "button") {
    return (
      <AuthGuard onLoggedIn={toggleFavorite} title="Sign in to save this listing">
        <Button
          variant={invert ? "ghost-dark" : "ghost"}
          onClick={handleButtonClick}
          disabled={
            createFavoriteListingMutation.isPending ||
            destroyFavoriteListingMutation.isPending
          }
          className="group"
        >
          <Icon
            icon="FavoriteSolid"
            className={cn("w-5", {
              "text-transparent": !favoriteListingId,
              "text-[#F43F5E] stroke-[#F43F5E]": favoriteListingId,
              "opacity-50":
                createFavoriteListingMutation.isPending ||
                destroyFavoriteListingMutation.isPending,
              "stroke-white": invert && !favoriteListingId,
              "stroke-black": !invert && !favoriteListingId,
            })}
            strokeWidth={2}
            overflow="visible"
          />

          <span className="hidden md:inline">
            {favoriteListingId ? unfavoriteLabel : favoriteLabel}
          </span>
        </Button>
      </AuthGuard>
    );
  }
}

ListingFavoriteButton.displayName = "ListingFavoriteButton";
